/**
 * Fiche Client 360 - Vue complète d'un client ETA
 * - Identité + analytique (CA, solde)
 * - Parcelles (lien bidirectionnel vers parcellaire)
 * - Devis, contrats, factures, règlements
 * - Échanges (notes, appels, emails, visites)
 * - Actions en attente (TODO)
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  ArrowLeft, Phone, Mail, MapPin, Loader2, Plus, FileText, Euro, MessageSquare, CheckSquare,
  Calendar, AlertCircle, Trash2, Edit, Map, FilePlus, ChevronRight, User, Briefcase, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const TYPE_COMM = {
  note:        { label: 'Note', icon: MessageSquare, color: 'bg-gray-100 text-gray-800' },
  appel:       { label: 'Appel', icon: Phone, color: 'bg-blue-100 text-blue-800' },
  email:       { label: 'Email', icon: Mail, color: 'bg-indigo-100 text-indigo-800' },
  visite:      { label: 'Visite', icon: User, color: 'bg-green-100 text-green-800' },
  rendezvous:  { label: 'Rendez-vous', icon: Calendar, color: 'bg-purple-100 text-purple-800' },
  sms:         { label: 'SMS', icon: MessageSquare, color: 'bg-amber-100 text-amber-800' },
};

const PRIORITE_COLOR = {
  urgente:  'bg-red-500 text-white',
  haute:    'bg-orange-500 text-white',
  normale:  'bg-blue-500 text-white',
  basse:    'bg-gray-400 text-white',
};

export default function FicheClient360Page() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddComm, setShowAddComm] = useState(false);
  const [showAddAction, setShowAddAction] = useState(false);
  const [newComm, setNewComm] = useState({ type: 'note', sujet: '', contenu: '', contact_nom: '', duree_minutes: '', important: false });
  const [newAction, setNewAction] = useState({ titre: '', description: '', type: 'rappel', priorite: 'normale', echeance: '' });
  const [showReplyEmail, setShowReplyEmail] = useState(false);
  const [replyData, setReplyData] = useState({ to: '', subject: '', body_html: '', enable_tracking: true });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [selectedParcelles, setSelectedParcelles] = useState([]);
  const [showPappersDialog, setShowPappersDialog] = useState(false);
  const [pappersSiren, setPappersSiren] = useState('');
  const [pappersLoading, setPappersLoading] = useState(false);

  const enrichirPappers = async () => {
    const clean = (pappersSiren || '').replace(/\D/g, '');
    if (clean.length !== 9 && clean.length !== 14) {
      toast.error("SIREN (9 chiffres) ou SIRET (14) requis");
      return;
    }
    setPappersLoading(true);
    try {
      const res = await api.post(`/pappers/enrichir-client/${clientId}?siren=${clean}`);
      toast.success(`Client enrichi avec Pappers · ${res.data.enriched.ville || ''}`);
      setShowPappersDialog(false);
      setPappersSiren('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec enrichissement Pappers");
    } finally { setPappersLoading(false); }
  };

  const createInterventionForParcelles = () => {
    const ids = selectedParcelles.join(',');
    navigate(`/interventions?client_id=${clientId}&parcelle_ids=${ids}`);
  };

  const openReplyEmail = (originalComm = null) => {
    const clientEmail = data?.client?.email || '';
    setReplyData({
      to: clientEmail,
      subject: originalComm?.sujet ? `Re: ${originalComm.sujet}` : '',
      body_html: originalComm?.contenu ? `<br/><br/>--- Message original ---<br/><blockquote>${(originalComm.contenu || '').replace(/\n/g, '<br/>')}</blockquote>` : '',
      enable_tracking: true,
      in_reply_to_msg_id: originalComm?.source_id || null,
    });
    setShowReplyEmail(true);
  };

  const sendEmail = async () => {
    if (!replyData.to || !replyData.subject || !replyData.body_html) {
      toast.error('Destinataire, sujet et contenu requis');
      return;
    }
    setSendingEmail(true);
    try {
      await api.post('/crm-email/envoyer', {
        client_id: clientId,
        to: replyData.to,
        subject: replyData.subject,
        body_html: replyData.body_html,
        enable_tracking: replyData.enable_tracking,
        in_reply_to_msg_id: replyData.in_reply_to_msg_id || null,
      });
      toast.success(replyData.enable_tracking ? 'Email envoyé · tracking activé' : 'Email envoyé');
      setShowReplyEmail(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setSendingEmail(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/crm-integre/clients/${clientId}/vue-complete`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Client introuvable');
    } finally { setLoading(false); }
  };
  useEffect(() => { if (clientId) fetchData(); }, [clientId]);

  const addComm = async () => {
    if (!newComm.contenu) { toast.error('Le contenu est requis'); return; }
    try {
      await api.post('/crm-communications/communications', {
        client_id: clientId,
        type: newComm.type,
        sujet: newComm.sujet || null,
        contenu: newComm.contenu,
        contact_nom: newComm.contact_nom || null,
        duree_minutes: newComm.duree_minutes ? parseInt(newComm.duree_minutes) : null,
        important: newComm.important,
      });
      toast.success('Échange ajouté');
      setShowAddComm(false);
      setNewComm({ type: 'note', sujet: '', contenu: '', contact_nom: '', duree_minutes: '', important: false });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const deleteComm = async (id) => {
    if (!window.confirm('Supprimer cet échange ?')) return;
    try {
      await api.delete(`/crm-communications/communications/${id}`);
      toast.success('Supprimé');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const addAction = async () => {
    if (!newAction.titre) { toast.error('Le titre est requis'); return; }
    try {
      await api.post('/crm-communications/actions', {
        client_id: clientId,
        ...newAction,
        echeance: newAction.echeance || null,
      });
      toast.success('Action créée');
      setShowAddAction(false);
      setNewAction({ titre: '', description: '', type: 'rappel', priorite: 'normale', echeance: '' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const updateActionStatut = async (action, newStatut) => {
    try {
      await api.put(`/crm-communications/actions/${action.id}`, { ...action, statut: newStatut });
      toast.success(newStatut === 'fait' ? '✓ Marqué comme fait' : 'Mis à jour');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  if (!data) return <div className="p-8 text-center">Client introuvable</div>;

  const { client, parcelles, interventions, contrats_ffl, contrats_eta, facturation, reglements, analytique, communications, actions_en_attente, devis } = data;
  const nomClient = client?.nom || client?.raison_sociale || `${client?.prenom || ''} ${client?.nom_famille || ''}`.trim() || 'Client';

  return (
    <div className="space-y-4 max-w-7xl" data-testid="fiche-client-360">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}><ArrowLeft className="w-5 h-5" /></Button>
          <div>
            <h1 className="text-3xl font-bold">{nomClient}</h1>
            <p className="text-sm text-muted-foreground flex items-center gap-3 flex-wrap mt-1">
              {client?.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {client.email}</span>}
              {client?.telephone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {client.telephone}</span>}
              {client?.adresse && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {client.adresse}</span>}
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => navigate(`/parcellaire?client_id=${clientId}`)} data-testid="btn-voir-parcelles">
            <Map className="w-4 h-4 mr-1" /> Voir parcelles ({parcelles?.nombre || 0})
          </Button>
          <Button variant="outline" onClick={() => setShowAddComm(true)} data-testid="btn-add-comm">
            <Plus className="w-4 h-4 mr-1" /> Échange
          </Button>
          <Button variant="outline" onClick={() => setShowAddAction(true)} data-testid="btn-add-action">
            <CheckSquare className="w-4 h-4 mr-1" /> Action
          </Button>
          <Button variant="outline" onClick={() => setShowPappersDialog(true)} data-testid="btn-enrichir-pappers" title="Enrichir avec Pappers (KBIS, adresse, dirigeants)">
            🇫🇷 Pappers/KBIS
          </Button>
        </div>
      </div>

      {/* Stats résumées */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card><CardContent className="p-3"><p className="text-xs text-muted-foreground">Chiffre d'affaires</p><p className="text-xl font-bold">{(analytique?.chiffre_affaires || 0).toLocaleString('fr-FR')} €</p></CardContent></Card>
        <Card className={analytique?.solde_client > 0 ? 'border-orange-300' : 'border-green-300'}>
          <CardContent className="p-3">
            <p className="text-xs text-muted-foreground">Solde</p>
            <p className={`text-xl font-bold ${analytique?.solde_client > 0 ? 'text-orange-600' : 'text-green-600'}`}>
              {(analytique?.solde_client || 0).toLocaleString('fr-FR')} €
            </p>
          </CardContent>
        </Card>
        <Card><CardContent className="p-3"><p className="text-xs text-muted-foreground">Heures prestées</p><p className="text-xl font-bold">{analytique?.heures_prestees || 0}h</p></CardContent></Card>
        <Card><CardContent className="p-3"><p className="text-xs text-muted-foreground">Surface</p><p className="text-xl font-bold">{(parcelles?.surface_totale_ha || 0).toFixed(1)} ha</p></CardContent></Card>
        <Card className={(actions_en_attente?.urgentes?.length || 0) > 0 ? 'border-red-300' : ''}>
          <CardContent className="p-3">
            <p className="text-xs text-muted-foreground">Actions en attente</p>
            <p className="text-xl font-bold">{actions_en_attente?.nombre || 0}{(actions_en_attente?.urgentes?.length || 0) > 0 && <span className="text-sm text-red-500 ml-1">⚡{actions_en_attente.urgentes.length}</span>}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="echanges" className="w-full">
        <TabsList className="grid grid-cols-3 md:grid-cols-6 w-full">
          <TabsTrigger value="echanges" data-testid="tab-echanges">📞 Échanges ({communications?.nombre || 0})</TabsTrigger>
          <TabsTrigger value="actions" data-testid="tab-actions">✓ Actions ({actions_en_attente?.nombre || 0})</TabsTrigger>
          <TabsTrigger value="parcelles" data-testid="tab-parcelles">🏞️ Parcelles ({parcelles?.nombre || 0})</TabsTrigger>
          <TabsTrigger value="devis" data-testid="tab-devis">📄 Devis ({devis?.nombre || 0})</TabsTrigger>
          <TabsTrigger value="contrats">📋 Contrats ({(contrats_eta?.nombre || 0) + (contrats_ffl?.nombre || 0)})</TabsTrigger>
          <TabsTrigger value="facturation">💰 Factures ({facturation?.nombre_factures || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="echanges" className="space-y-2 mt-4">
          <div className="flex justify-end gap-2 mb-2">
            <Button size="sm" variant="outline" onClick={() => openReplyEmail(null)} data-testid="btn-new-email">
              <Mail className="w-4 h-4 mr-1" /> Envoyer un email
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowAddComm(true)} data-testid="btn-new-comm">
              <Plus className="w-4 h-4 mr-1" /> Ajouter un échange
            </Button>
          </div>
          {(communications?.liste || []).length === 0 ? (
            <Card><CardContent className="p-6 text-center text-muted-foreground">
              <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>Aucun échange enregistré.</p>
              <Button className="mt-2" onClick={() => setShowAddComm(true)}>+ Ajouter un échange</Button>
            </CardContent></Card>
          ) : communications.liste.map(c => {
            const meta = TYPE_COMM[c.type] || TYPE_COMM.note;
            const Icon = meta.icon;
            const isEmail = c.type === 'email';
            return (
              <Card key={c.id} className={c.important ? 'border-amber-400' : ''} data-testid={`comm-card-${c.id}`}>
                <CardContent className="p-3 flex items-start gap-3">
                  <div className={`p-2 rounded ${meta.color}`}><Icon className="w-4 h-4" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline">{meta.label}</Badge>
                      {c.direction === 'sortant' && <Badge className="bg-blue-500 text-white">↗ Envoyé</Badge>}
                      {c.direction === 'entrant' && <Badge className="bg-emerald-500 text-white">↙ Reçu</Badge>}
                      {c.tracking_enabled && c.opens_count > 0 && (
                        <Badge className="bg-emerald-600 text-white" data-testid={`badge-opened-${c.id}`} title={`${c.opens_count} ouverture(s)${c.first_opened_at ? ' · 1ère le ' + new Date(c.first_opened_at).toLocaleString('fr-FR') : ''}`}>
                          ✓ Ouvert ({c.opens_count})
                        </Badge>
                      )}
                      {c.tracking_enabled && c.opens_count === 0 && (
                        <Badge variant="outline" data-testid={`badge-not-opened-${c.id}`}>📭 Non ouvert</Badge>
                      )}
                      {c.important && <Badge className="bg-amber-500">⭐ Important</Badge>}
                      {c.sujet && <span className="font-semibold">{c.sujet}</span>}
                      <span className="text-xs text-muted-foreground ml-auto">
                        {c.date_evenement ? new Date(c.date_evenement).toLocaleString('fr-FR') : ''}
                      </span>
                    </div>
                    <p className="text-sm mt-1 whitespace-pre-wrap line-clamp-4">{(c.contenu || '').replace(/<[^>]+>/g, ' ').slice(0, 600)}</p>
                    <div className="text-xs text-muted-foreground mt-1 flex gap-3 flex-wrap items-center">
                      {c.contact_nom && <span>👤 {c.contact_nom}</span>}
                      {c.duree_minutes && <span>⏱️ {c.duree_minutes} min</span>}
                      {isEmail && (
                        <Button size="sm" variant="outline" className="h-6" onClick={() => openReplyEmail(c)} data-testid={`btn-reply-email-${c.id}`}>
                          <Mail className="w-3 h-3 mr-1" /> Répondre
                        </Button>
                      )}
                    </div>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => deleteComm(c.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        <TabsContent value="actions" className="space-y-2 mt-4">
          {(actions_en_attente?.liste || []).length === 0 ? (
            <Card><CardContent className="p-6 text-center text-muted-foreground">
              <CheckSquare className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>Aucune action en attente.</p>
              <Button className="mt-2" onClick={() => setShowAddAction(true)}>+ Créer une action</Button>
            </CardContent></Card>
          ) : actions_en_attente.liste.map(a => (
            <Card key={a.id} className={a.priorite === 'urgente' ? 'border-red-400' : ''}>
              <CardContent className="p-3 flex items-start gap-3">
                <input
                  type="checkbox"
                  className="w-5 h-5 mt-1"
                  checked={a.statut === 'fait'}
                  onChange={() => updateActionStatut(a, a.statut === 'fait' ? 'a_faire' : 'fait')}
                  data-testid={`check-action-${a.id}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`font-semibold ${a.statut === 'fait' ? 'line-through opacity-60' : ''}`}>{a.titre}</span>
                    <Badge className={PRIORITE_COLOR[a.priorite]}>{a.priorite}</Badge>
                    <Badge variant="outline">{a.type}</Badge>
                    {a.echeance && (
                      <Badge variant="outline" className="gap-1"><Clock className="w-3 h-3" />{new Date(a.echeance).toLocaleDateString('fr-FR')}</Badge>
                    )}
                  </div>
                  {a.description && <p className="text-sm mt-1 text-muted-foreground">{a.description}</p>}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="parcelles" className="space-y-2 mt-4" data-testid="tab-parcelles-content">
          {(parcelles?.liste || []).length === 0 ? (
            <Card><CardContent className="p-6 text-center text-muted-foreground">Aucune parcelle liée à ce client.</CardContent></Card>
          ) : (
            <div className="space-y-3">
              {/* Récapitulatif assolement */}
              <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200">
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-xs text-emerald-700 font-medium">ASSOLEMENT TOTAL</p>
                      <p className="text-2xl font-bold text-emerald-900">{(parcelles?.surface_totale_ha || 0).toFixed(2)} ha</p>
                      <p className="text-xs text-emerald-700">{parcelles.liste.length} parcelle{parcelles.liste.length > 1 ? 's' : ''}</p>
                    </div>
                    {selectedParcelles.length > 0 && (
                      <div className="text-right">
                        <p className="text-xs text-emerald-700 font-medium">SÉLECTION</p>
                        <p className="text-xl font-bold text-emerald-700">
                          {parcelles.liste.filter(p => selectedParcelles.includes(p.id)).reduce((s, p) => s + (p.surface_ha || p.surface || 0), 0).toFixed(2)} ha
                        </p>
                        <p className="text-xs text-emerald-700">{selectedParcelles.length} sélectionnée{selectedParcelles.length > 1 ? 's' : ''}</p>
                      </div>
                    )}
                    <div className="flex flex-col gap-1">
                      {selectedParcelles.length > 0 && (
                        <>
                          <Button size="sm" onClick={() => createInterventionForParcelles()} data-testid="btn-creer-intervention-parcelles">
                            <Plus className="w-3 h-3 mr-1" /> Créer intervention ({selectedParcelles.length})
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setSelectedParcelles([])}>
                            Désélectionner
                          </Button>
                        </>
                      )}
                      {selectedParcelles.length === 0 && (
                        <Button size="sm" variant="outline" onClick={() => setSelectedParcelles(parcelles.liste.map(p => p.id))} data-testid="btn-selectionner-toutes-parcelles">
                          Tout sélectionner
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Répartition par culture */}
                  {(() => {
                    const parCulture = {};
                    for (const p of parcelles.liste) {
                      const c = p.culture_actuelle || p.culture || p.culture_nom || 'Non défini';
                      parCulture[c] = (parCulture[c] || 0) + (p.surface_ha || p.surface || 0);
                    }
                    const cultures = Object.entries(parCulture).sort((a, b) => b[1] - a[1]).slice(0, 6);
                    if (cultures.length === 0) return null;
                    return (
                      <div className="mt-3 pt-3 border-t border-emerald-200">
                        <p className="text-xs font-semibold text-emerald-700 mb-1">RÉPARTITION PAR CULTURE</p>
                        <div className="flex flex-wrap gap-1.5">
                          {cultures.map(([culture, ha]) => (
                            <Badge key={culture} variant="outline" className="bg-white text-xs">
                              {culture} : <strong className="ml-1">{ha.toFixed(1)} ha</strong>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* Liste parcelles avec checkbox */}
              {parcelles.liste.map(p => {
                const isSel = selectedParcelles.includes(p.id);
                return (
                  <Card key={p.id} className={`hover:bg-accent transition-colors ${isSel ? 'border-emerald-500 bg-emerald-50/40' : ''}`} data-testid={`parcelle-row-${p.id}`}>
                    <CardContent className="p-3 flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={isSel}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedParcelles([...selectedParcelles, p.id]);
                          else setSelectedParcelles(selectedParcelles.filter(id => id !== p.id));
                        }}
                        className="w-4 h-4"
                        data-testid={`check-parcelle-${p.id}`}
                      />
                      <div className="flex-1 cursor-pointer" onClick={() => navigate(`/parcellaire?parcelle_id=${p.id}`)}>
                        <p className="font-semibold flex items-center gap-2">
                          <MapPin className="w-3.5 h-3.5 text-emerald-600" />
                          {p.nom}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {(p.surface_ha || p.surface || 0).toFixed(2)} ha · {p.culture_actuelle || p.culture || p.culture_nom || 'Culture non définie'}
                          {p.exploitation_nom && <> · {p.exploitation_nom}</>}
                        </p>
                      </div>
                      <Button size="icon" variant="ghost" onClick={() => navigate(`/parcellaire?parcelle_id=${p.id}`)} title="Voir sur la carte" data-testid={`btn-voir-parcelle-${p.id}`}>
                        <ChevronRight className="w-5 h-5" />
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="devis" className="space-y-2 mt-4">
          {(devis?.liste || []).length === 0 ? (
            <Card><CardContent className="p-6 text-center text-muted-foreground">Aucun devis pour ce client.</CardContent></Card>
          ) : devis.liste.map(d => (
            <Card key={d.id || d.numero}>
              <CardContent className="p-3 flex items-center justify-between">
                <div>
                  <p className="font-semibold">{d.numero || 'Devis sans numéro'}</p>
                  <p className="text-xs text-muted-foreground">
                    {d.objet || d.description || ''}
                    {d.montant_ttc && ` · ${d.montant_ttc.toLocaleString('fr-FR')} € TTC`}
                  </p>
                </div>
                <Badge>{d.statut || 'brouillon'}</Badge>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="contrats" className="space-y-2 mt-4">
          {[...(contrats_eta?.liste || []), ...(contrats_ffl?.liste || []).map(c => ({ ...c, _type: 'FFL' }))].length === 0 ? (
            <Card><CardContent className="p-6 text-center text-muted-foreground">Aucun contrat signé.</CardContent></Card>
          ) : [...(contrats_eta?.liste || []), ...(contrats_ffl?.liste || []).map(c => ({ ...c, _type: 'FFL' }))].map(c => (
            <Card key={c.id || c.numero}>
              <CardContent className="p-3 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold">{c.titre || c.numero || 'Contrat'}</p>
                    {c._type && <Badge variant="outline">{c._type}</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground">{c.description || c.objet || ''}</p>
                </div>
                <Badge>{c.statut || c.signe ? 'Signé' : 'En attente'}</Badge>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="facturation" className="space-y-2 mt-4">
          {(facturation?.factures || []).length === 0 ? (
            <Card><CardContent className="p-6 text-center text-muted-foreground">Aucune facture.</CardContent></Card>
          ) : facturation.factures.map(f => (
            <Card key={f.id || f.numero}>
              <CardContent className="p-3 flex items-center justify-between">
                <div>
                  <p className="font-semibold">{f.numero || f.numero_facture}</p>
                  <p className="text-xs text-muted-foreground">{f.date_emission || f.date_facture} · {f.montant_ttc?.toLocaleString('fr-FR') || f.total_ttc?.toLocaleString('fr-FR')} €</p>
                </div>
                <Badge variant={f.statut === 'payee' ? 'default' : 'outline'}>{f.statut || 'brouillon'}</Badge>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* Dialog ajout échange */}
      <Dialog open={showAddComm} onOpenChange={setShowAddComm}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouvel échange</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Type</Label>
              <Select value={newComm.type} onValueChange={v => setNewComm({ ...newComm, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(TYPE_COMM).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Sujet</Label><Input value={newComm.sujet} onChange={e => setNewComm({ ...newComm, sujet: e.target.value })} /></div>
            <div><Label>Contenu *</Label><Textarea rows={4} value={newComm.contenu} onChange={e => setNewComm({ ...newComm, contenu: e.target.value })} data-testid="input-comm-contenu" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Contact (à qui)</Label><Input value={newComm.contact_nom} onChange={e => setNewComm({ ...newComm, contact_nom: e.target.value })} /></div>
              <div><Label>Durée (min)</Label><Input type="number" value={newComm.duree_minutes} onChange={e => setNewComm({ ...newComm, duree_minutes: e.target.value })} /></div>
            </div>
            <label className="flex items-center gap-2"><input type="checkbox" checked={newComm.important} onChange={e => setNewComm({ ...newComm, important: e.target.checked })} /> Important</label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddComm(false)}>Annuler</Button>
            <Button onClick={addComm} data-testid="btn-save-comm">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog ajout action */}
      <Dialog open={showAddAction} onOpenChange={setShowAddAction}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouvelle action / TODO</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Titre *</Label><Input value={newAction.titre} onChange={e => setNewAction({ ...newAction, titre: e.target.value })} data-testid="input-action-titre" placeholder="Ex: Relancer pour devis labour" /></div>
            <div><Label>Description</Label><Textarea rows={3} value={newAction.description} onChange={e => setNewAction({ ...newAction, description: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Type</Label>
                <Select value={newAction.type} onValueChange={v => setNewAction({ ...newAction, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rappel">Rappel</SelectItem>
                    <SelectItem value="relance">Relance</SelectItem>
                    <SelectItem value="devis">Devis</SelectItem>
                    <SelectItem value="rendezvous">Rendez-vous</SelectItem>
                    <SelectItem value="facturation">Facturation</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Priorité</Label>
                <Select value={newAction.priorite} onValueChange={v => setNewAction({ ...newAction, priorite: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basse">Basse</SelectItem>
                    <SelectItem value="normale">Normale</SelectItem>
                    <SelectItem value="haute">Haute</SelectItem>
                    <SelectItem value="urgente">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Échéance</Label><Input type="date" value={newAction.echeance} onChange={e => setNewAction({ ...newAction, echeance: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddAction(false)}>Annuler</Button>
            <Button onClick={addAction} data-testid="btn-save-action">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Envoi / Réponse Email avec tracking */}
      <Dialog open={showReplyEmail} onOpenChange={setShowReplyEmail}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5" /> Envoyer un email au client</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Destinataire *</Label>
              <Input type="email" value={replyData.to} onChange={e => setReplyData({ ...replyData, to: e.target.value })} data-testid="input-email-to" />
            </div>
            <div>
              <Label>Sujet *</Label>
              <Input value={replyData.subject} onChange={e => setReplyData({ ...replyData, subject: e.target.value })} data-testid="input-email-subject" />
            </div>
            <div>
              <Label>Message (HTML autorisé)</Label>
              <Textarea
                rows={8}
                value={replyData.body_html}
                onChange={e => setReplyData({ ...replyData, body_html: e.target.value })}
                data-testid="input-email-body"
                placeholder="Bonjour,&#10;&#10;..."
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={replyData.enable_tracking}
                onChange={e => setReplyData({ ...replyData, enable_tracking: e.target.checked })}
                data-testid="check-enable-tracking"
              />
              📡 Activer le tracking d'ouverture (pixel invisible)
            </label>
            <p className="text-xs text-muted-foreground">
              L'email sera envoyé depuis la boîte Gmail de votre société. Si activé, vous verrez "✓ Ouvert" lorsque le client ouvrira l'email.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReplyEmail(false)}>Annuler</Button>
            <Button onClick={sendEmail} disabled={sendingEmail} data-testid="btn-send-email">
              {sendingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Pappers / KBIS - enrichissement client */}
      <Dialog open={showPappersDialog} onOpenChange={setShowPappersDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">🇫🇷 Enrichir avec Pappers / KBIS</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Renseigne le SIREN (9 chiffres) ou SIRET (14 chiffres) du client. L'application va automatiquement récupérer :
            </p>
            <ul className="text-xs space-y-1 text-muted-foreground pl-4">
              <li>• Adresse complète + coordonnées GPS (pour la carte)</li>
              <li>• Forme juridique, capital, code APE, activité</li>
              <li>• N° TVA intracommunautaire</li>
              <li>• Dirigeants & date de création</li>
              <li>• Lien direct vers le KBIS Pappers</li>
            </ul>
            <div>
              <Label>SIREN ou SIRET *</Label>
              <Input
                placeholder="Ex: 552 100 554 ou 55210055400029"
                value={pappersSiren}
                onChange={e => setPappersSiren(e.target.value)}
                data-testid="input-pappers-siren"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Recherche-le sur <a href={`https://www.pappers.fr/recherche?q=${encodeURIComponent(client?.nom || '')}`} target="_blank" rel="noopener noreferrer" className="text-primary underline">Pappers.fr ↗</a> si tu ne le connais pas.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPappersDialog(false)}>Annuler</Button>
            <Button onClick={enrichirPappers} disabled={pappersLoading} data-testid="btn-confirm-pappers">
              {pappersLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : '🇫🇷'}
              Enrichir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
